import React from "react";
import { Plus, Minus } from "lucide-react";
import { Section } from "../../../utils/Customer/CustomerTypes";

interface AccordionItemProps {
  section: Section;
  isOpen: boolean;
  onClick: () => void;
}

const AccordionItem: React.FC<AccordionItemProps> = ({
  section,
  isOpen,
  onClick,
}) => {
  const renderContent = () => {
    switch (section.type) {
      // =====================================================
      // TEXT CONTENT
      // =====================================================
      case "text":
        return (
          <div className="text-sm text-gray-600 leading-7">
            <p className="whitespace-pre-line">{section.data}</p>
          </div>
        );

      // =====================================================
      // TABLE CONTENT
      // =====================================================
      case "table":
        if (!Array.isArray(section.data) || section.data.length === 0) {
          return (
            <p className="text-sm text-gray-400 py-2">
              No information available.
            </p>
          );
        }

        return (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="divide-y divide-slate-100">
              {section.data.map((item: any, i: number) => (
                <div
                  key={`${item.label}-${i}`}
                  className="
                                        grid
                                        grid-cols-1
                                        sm:grid-cols-[40%_60%]
                                        gap-1
                                        sm:gap-4
                                        px-4
                                        py-3
                                        sm:px-5
                                        sm:py-3.5
                                        hover:bg-slate-50/70
                                        transition-colors
                                    "
                >
                  {/* Label */}
                  <div
                    className={`
                                            text-xs
                                            sm:text-sm
                                            font-medium
                                            text-slate-500
                                            ${item.isSub ? "pl-4" : ""}
                                        `}
                  >
                    {item.label}
                  </div>

                  {/* Value */}
                  <div className="text-sm font-semibold text-slate-800 break-words">
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className={`
                overflow-hidden
                rounded-xl
                border
                transition-all
                duration-300
                ${
                  isOpen
                    ? "border-mainBrandColor/30 bg-white shadow-sm"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }
            `}
    >
      {/* =================================================
                HEADER
            ================================================== */}
      <button
        type="button"
        onClick={onClick}
        aria-expanded={isOpen}
        className="
                    w-full
                    flex
                    items-center
                    justify-between
                    gap-4
                    px-4
                    py-3.5
                    sm:px-5
                    sm:py-4
                    text-left
                    transition-colors
                    duration-200
                    hover:bg-slate-50/70
                    focus:outline-none
                "
      >
        {/* Title */}
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`
                            w-1.5
                            h-5
                            rounded-full
                            shrink-0
                            transition-colors
                            duration-300
                            ${isOpen ? "bg-mainBrandColor" : "bg-slate-200"}
                        `}
          />

          <span
            className={`
                            text-sm
                            sm:text-[15px]
                            font-semibold
                            tracking-tight
                            ${isOpen ? "text-logoHeading" : "text-slate-700"}
                        `}
          >
            {section.title}
          </span>
        </div>

        {/* Toggle */}
        <span
          className={`
                        flex
                        items-center
                        justify-center
                        w-7
                        h-7
                        rounded-full
                        shrink-0
                        transition-all
                        duration-300
                        ${
                          isOpen
                            ? "bg-mainBrandColor text-white"
                            : "bg-slate-100 text-slate-500"
                        }
                    `}
        >
          {isOpen ? (
            <Minus size={15} strokeWidth={2.5} />
          ) : (
            <Plus size={15} strokeWidth={2.5} />
          )}
        </span>
      </button>

      {/* =================================================
                CONTENT
            ================================================== */}
      <div
        className={`
                    grid
                    transition-[grid-template-rows,opacity]
                    duration-300
                    ease-in-out
                    ${
                      isOpen
                        ? "grid-rows-[1fr] opacity-100"
                        : "grid-rows-[0fr] opacity-0"
                    }
                `}
      >
        <div className="overflow-hidden">
          <div
            className="
                            border-t
                            border-slate-100
                            px-4
                            py-4
                            sm:px-5
                            sm:py-5
                        "
          >
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccordionItem;
